import { ActionTree, Commit, GetterTree, Module, MutationTree } from "vuex";
import { IEventsState } from "./types";
import { IIDAStore } from "../types";
import { endOfDay, startOfDay, isSameDay } from "date-fns";
import {
  Events,
  ICreateRecurrentEvents,
  IDeleteEventsInPeriod,
  IDeleteEventsOnDates,
  IEventsPost,
  IPostRecurrentEvents,
  IPutRecurrentEvents
} from "../../models/events";
import { eventsService } from "../../services/events.service";
import { eCode, EventTypes } from "../../models/eventtypes";
import { EventsPostback, RecurrenceErrorPostback } from "../../models/eventsPostback-models";
import { StoreCache, storeCachePeriode } from "../storeCache";
import { IPeriode } from "../../utils/dateUtils";
import { invalidateClosedDayEvents } from "../../utils/queryInvalidation";
import { reactive } from "vue";

export const getInitialState = (): IEventsState => {
  return {
    searching: false,
    events: new Map(),
    eventsKeys: new Map(),
    eventTypes: [],
    eventsStoreCache: new StoreCache()
  };
};

export const employeeIdForEventsWithoutPersons = -999;

const namespaced = true;

export const getters: GetterTree<IEventsState, IIDAStore> = {
  GetEvents: (state: IEventsState) => (from: Date, to: Date) => {
    return Object.values(state.events).filter(
      (events: Events) => events.start_time <= to && events.end_time > from
    );
  },
  GetCloseDayEventypeID: (state: IEventsState) => {
    return (
      state.eventTypes.find((eventtypes: EventTypes) => eventtypes.code === eCode.ClosedEvent)
        ?.id || 0
    );
  },
  GetPupilFreeID: (state: IEventsState) => {
    return (
      state.eventTypes.find(
        (eventtypes: EventTypes) => eventtypes.code === eCode.PupilFreeTimeEvent
      )?.id || 0
    );
  },
  GetEventById: (state: IEventsState) => (eventId: number) => {
    return state.events[eventId];
  },
  // Participation is derived from duties (duty.event_id + duty.employee_id) —
  // each participant has exactly one duty per event — so we no longer need a
  // separate event_participations fetch.
  GetEventsByEmployeeId:
    (state, _getters, _rootState, rootGetters) => (employeeId: number) => {
      const eventIds = eventIdsFromDuties(rootGetters, employeeId);
      if (employeeId !== employeeIdForEventsWithoutPersons) {
        const events = (Object.values(state.events) as Events[]).filter((event) =>
          eventIds.has(event.id)
        );
        return events.sort((a, b) => (a.start_time > b.start_time ? 1 : -1));
      }
      const events = (Object.values(state.events) as Events[]).filter(
        (event) => !eventIds.has(event.id)
      );
      return events.sort((a, b) => (a.start_time > b.start_time ? 1 : -1));
    },
  GetEventsByDate:
    (state) =>
      (dato: Date): Events[] => {
        let events = state.eventsKeys[startOfDay(dato).getTime()];
        if (!events) {
          events = reactive([]);
        }
        return Object.values(events);
      },
  GetEventsByEmployeeIdAndDate:
    (_state, getters, _rootState, rootGetters) =>
      (employeeId: number, dato: Date) => {
        const events: Events[] = getters.GetEventsByDate(dato);
        // Only scan the duties on this date (keyed lookup) instead of the whole
        // duties store — events on a date are linked by duties on the same date.
        const dutiesOnDate = rootGetters["duties/GetDutiesByDate"](dato) as {
          event_id: number | null;
          employee_id: number;
        }[];
        const isWithoutPersons = employeeId === employeeIdForEventsWithoutPersons;
        const eventIds = new Set<number>();
        for (const duty of dutiesOnDate) {
          if (duty.event_id == null) {
            continue;
          }
          if (isWithoutPersons || duty.employee_id === employeeId) {
            eventIds.add(duty.event_id);
          }
        }
        const list: Events[] = reactive([]);
        for (const event of events) {
          if (isWithoutPersons ? !eventIds.has(event.id) : eventIds.has(event.id)) {
            list.push(event);
          }
        }
        return list;
      }
};

// Builds the set of event ids the employee participates in, derived from the
// duties store. For employeeIdForEventsWithoutPersons it instead returns the set
// of all event ids that have any duty, so callers can pick the events with none.
function eventIdsFromDuties(
  rootGetters: Record<string, any>,
  employeeId: number
): Set<number> {
  const duties = rootGetters["duties/GetDuties"]() as {
    event_id: number | null;
    employee_id: number;
  }[];
  const ids = new Set<number>();
  const allWithDuties = employeeId === employeeIdForEventsWithoutPersons;
  for (const duty of duties) {
    if (duty.event_id == null) {
      continue;
    }
    if (allWithDuties || duty.employee_id === employeeId) {
      ids.add(duty.event_id);
    }
  }
  return ids;
}

export const actions: ActionTree<IEventsState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
    // Keep the closed-day (lukkedage) query cache in sync with the events store,
    // so counts recompute after calendar events are added/edited/deleted.
    invalidateClosedDayEvents();
  },
  deleteRecurrentEvents({ commit, state, rootGetters }, eventId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteRecurrentEvents(eventId)
        .then((response) => {
          // The endpoint deletes the whole series, so drop every event in it —
          // not just the one the dialog happened to be opened on.
          // -1 / 0 is the "not part of a series" marker (see Events#isRecurrentEvent).
          const recurrentEventId = state.events[eventId]?.recurrent_event_id;
          const eventIds =
            recurrentEventId && recurrentEventId > 0
              ? eventIdsMatching(state, (e) => e.recurrent_event_id === recurrentEventId)
              : [eventId];
          removeEventsAndTheirDuties(commit, rootGetters, eventIds);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEventsInPeriod({ commit, state, rootGetters }, event: IDeleteEventsInPeriod): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteRecurrentEventsInPeriod(event)
        .then((response) => {
          const eventIds = eventIdsMatching(
            state,
            (e) =>
              e.recurrent_event_id === event.recurrent_event_id &&
              e.start_time >= event.from_date &&
              e.start_time <= event.to_date
          );
          removeEventsAndTheirDuties(commit, rootGetters, eventIds);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEventsOnDates({ commit, state, rootGetters }, event: IDeleteEventsOnDates): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteRecurrentEventsOnDates(event)
        .then((response) => {
          const eventIds = eventIdsMatching(
            state,
            (e) =>
              e.recurrent_event_id === event.recurrent_event_id &&
              event.dates.some((date) => isSameDay(date, e.start_time))
          );
          removeEventsAndTheirDuties(commit, rootGetters, eventIds);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  deleteEvents({ commit, rootGetters }, eventId: number): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteEvents(eventId)
        .then((response) => {
          removeEventsAndTheirDuties(commit, rootGetters, [eventId]);
          resolve(response);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  createEvents({ commit }, event: IEventsPost): Promise<Events> {
    return new Promise((resolve, reject) => {
      eventsService
        .createEvents(event)
        .then((newEvent: EventsPostback) => {
          commit("updateEvents", { events: [newEvent.event], from: new Date(), to: new Date() });
          commit("duties/updateDuties", { duties: newEvent.duties }, { root: true });
          invalidateClosedDayEvents();
          resolve(newEvent.event);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  updateEvents({ commit }, event: IEventsPost): Promise<Events> {
    return new Promise((resolve, reject) => {
      eventsService
        .updateEvents(event)
        .then((newEvent: EventsPostback) => {
          commit("updateEvents", { events: [newEvent.event], from: new Date(), to: new Date() });
          commit("duties/deleteDuties", newEvent.deleted_duties, { root: true });
          commit("duties/updateDuties", { duties: newEvent.duties }, { root: true });
          invalidateClosedDayEvents();
          resolve(newEvent.event);
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  fetchEvents({ commit, state }, query: { from: Date; to: Date }): Promise<Events[]> {
    return new Promise((resolve, reject) => {
      const from: Date = startOfDay(query.from);
      const to: Date = endOfDay(query.to);
      const periode = { fromDate: from, toDate: to } as IPeriode;
      if (!state.eventsStoreCache.isCached(periode)) {
        try {
          eventsService.fetchEvents(from, to).then((events) => {
            commit("updateEvents", { events, from, to });
            return resolve(events);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        const events: Events[] = Object.values(state.events).filter(
          (ab) => ab.end_time >= from && ab.start_time < to
        );
        return resolve(events);
      }
    });
  },
  fetchEventsByRecurrenceId({ state }, query: { recurrenceId: number }): Promise<Events[]> {
    return new Promise((resolve, reject) => {
      try {
        const events: Events[] = Object.values(state.events).filter(
          (ab: Events) => ab.recurrent_event_id === query.recurrenceId
        );
        if (events.length > 0) {
          events.sort((a: Events, b: Events) => (a.start_time > b.start_time ? 1 : -1));
          return resolve(events);
        } else {
          eventsService.fetchEventsByRecurrenceId(query.recurrenceId).then((events) => {
            return resolve(events);
          });
        }
      } catch (error) {
        return reject(error);
      }
    });
  },
  fetchEventTypes({ commit, state }): Promise<EventTypes[]> {
    return new Promise((resolve, reject) => {
      if (state.eventTypes.length == 0) {
        eventsService
          .fetchEventTypes()
          .then((eventstypes) => {
            commit("updateEventTypes", { eventTypes: eventstypes });
            return resolve(eventstypes);
          })
          .catch((error) => {
            return reject(error);
          });
      } else {
        return resolve(state.eventTypes);
      }
    });
  },

  updateEventType({ commit }, eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      eventsService
        .updateEventTypes(eventType)
        .then((eventstypes) => {
          commit("updateEventTypes", { eventTypes: [eventstypes] });
          return resolve(eventstypes);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },
  createEventType({ commit }, eventType: EventTypes): Promise<EventTypes> {
    return new Promise((resolve, reject) => {
      eventsService
        .createEventType(eventType)
        .then((eventstypes) => {
          commit("updateEventTypes", { eventTypes: [eventstypes] });
          return resolve(eventstypes);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },
  deleteEventType({ commit }, eventType: EventTypes): Promise<void> {
    return new Promise((resolve, reject) => {
      eventsService
        .deleteEventType(eventType)
        .then(() => {
          commit("removeEventType", eventType.id);
          return resolve();
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },

  addDatesToRecurrentEvent(
    { commit },
    query: { firstEventDate: Date; recurrentInfo: IPostRecurrentEvents }
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .addDatesToRecurrentEvent(query.recurrentInfo)
        .then((postback: RecurrenceErrorPostback) => {
          // The endpoint returns the newly created events + duties, so add them
          // to the store incrementally instead of refetching everything.
          commit("updateEvents", {
            events: postback.events,
            from: new Date(),
            to: new Date()
          });
          commit(
            "duties/updateDuties",
            { duties: postback.duties },
            { root: true }
          );
          invalidateClosedDayEvents();
          resolve(postback);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },

  createRecurrentEvents(
    { commit },
    recurrentInfo: ICreateRecurrentEvents
  ): Promise<RecurrenceErrorPostback> {
    return new Promise((resolve, reject) => {
      eventsService
        .createRecurrentEvents(recurrentInfo)
        .then((postback: RecurrenceErrorPostback) => {
          // The endpoint returns the newly created events + duties, so add them
          // to the store incrementally instead of refetching everything.
          commit("updateEvents", {
            events: postback.events,
            from: new Date(),
            to: new Date()
          });
          commit(
            "duties/updateDuties",
            { duties: postback.duties },
            { root: true }
          );
          invalidateClosedDayEvents();
          resolve(postback);
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },

  updateRecurrentEvents({ commit }, recurrentInfo: IPutRecurrentEvents): Promise<any> {
    return new Promise((resolve, reject) => {
      eventsService
        .updateRecurrentEvents(recurrentInfo)
        .then((postback: RecurrenceErrorPostback) => {
          // The endpoint returns the updated events + duties (and ids of duties
          // removed, e.g. when a participant is removed), so apply them to the
          // store incrementally instead of refetching everything.
          commit("updateEvents", {
            events: postback.events,
            from: new Date(),
            to: new Date()
          });
          if (postback.deleted_duties.length > 0) {
            commit("duties/deleteDuties", postback.deleted_duties, {
              root: true
            });
          }
          commit(
            "duties/updateDuties",
            { duties: postback.duties },
            { root: true }
          );
          invalidateClosedDayEvents();
          resolve(postback);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export const mutations: MutationTree<IEventsState> = {
  reset(state: IEventsState): void {
    Object.assign(state, getInitialState());
  },
  deleteEventsByIds(state: IEventsState, eventIds: number[]): void {
    eventIds.forEach((eventId) => removeFromState(state, eventId));
  },
  updateEvents(state: IEventsState, payload: { events: Events[]; from: Date; to: Date }): void {
    for (let index = 0; index < payload.events.length; index++) {
      const event: Events = payload.events[index];
      const olddate = state.events[event.id]?.start_time;
      state.events[event.id] = event;

      // event keys
      if (olddate && olddate.getTime() != event.start_time.getTime()) {
        delete state.eventsKeys[startOfDay(olddate).getTime()][event.id];
      }
      setKey(state.eventsKeys, startOfDay(event.start_time).getTime(), event);
    }

    if (payload.from && payload.to) {
      state.eventsStoreCache.addCache(
        new storeCachePeriode({ fromDate: payload.from, toDate: payload.to })
      );
    }
  },
  updateEventTypes(state: IEventsState, payload: { eventTypes: EventTypes[] }): void {
    state.eventTypes = [
      ...payload.eventTypes,
      ...state.eventTypes.filter(
        (oldEventTypes) => !payload.eventTypes.find((existing) => existing.equals(oldEventTypes))
      )
    ];
  },
  deleteEventType(state: IEventsState, payload: { eventType: EventTypes }): void {
    state.eventTypes = state.eventTypes.filter((existing) => existing.id !== payload.eventType.id);
  },
  removeEventType(state: IEventsState, eventTypeId: number): void {
    state.eventTypes = state.eventTypes.filter((existing) => existing.id !== eventTypeId);
  }
};

export const eventsStoreModule: Module<IEventsState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};

// The events are written with object semantics (state.events[id] = event), so
// they must be read the same way — Map#values() never sees those entries.
function eventsInState(state: IEventsState): Events[] {
  return Object.values(state.events) as Events[];
}

function eventIdsMatching(
  state: IEventsState,
  predicate: (event: Events) => boolean
): number[] {
  return eventsInState(state)
    .filter(predicate)
    .map((event) => event.id);
}

// Deleting an event must also drop its duties: the actual plan and the pool
// overview build their rows from the duties store, so a duty left behind keeps
// a deleted activity (and its pool consumption) on screen until a full reload.
function removeEventsAndTheirDuties(
  commit: Commit,
  rootGetters: Record<string, any>,
  eventIds: number[]
): void {
  if (eventIds.length === 0) {
    return;
  }
  const deletedIds = new Set(eventIds);
  const dutyIds = (rootGetters["duties/GetDuties"]() as {
    id: number;
    event_id: number | null;
  }[])
    .filter((duty) => duty.event_id !== null && deletedIds.has(duty.event_id))
    .map((duty) => duty.id);

  commit("deleteEventsByIds", eventIds);
  if (dutyIds.length > 0) {
    commit("duties/deleteDuties", dutyIds, { root: true });
  }
  // Closed-day (lukkedage) counts live in their own query cache.
  invalidateClosedDayEvents();
}

function setKey(statevalue, key, value) {
  const valueFromKey = statevalue[key];
  if (!valueFromKey) {
    statevalue[key] = { [value.id]: value };
  } else {
    valueFromKey[value.id] = value;
  }
}

function removeFromState(state: IEventsState, eventId: number) {
  const oldEvent = state.events[eventId];
  if (oldEvent) {
    const eventsOnDate = state.eventsKeys[startOfDay(oldEvent.start_time).getTime()];
    if (eventsOnDate) {
      delete eventsOnDate[eventId];
    }
  }
  delete state.events[eventId];
}
